/**
 * Snowplow events and Katal logs for the session-wide capture consent screen.
 *
 * Kept out of `AIWorkflowMetricsHelper`, whose category is always `ConnectToVpn`. Every event
 * carries the terms version, the only thing separating this screen from the 2.0 prompt.
 */
import { trackStructEvent } from "@snowplow/browser-tracker";

import { Logger } from "@/apis/Logger";
import {
  type AiConsent,
  AI_CONSENT_TERMS_VERSION,
} from "@/components/AIAutomation/ai-consent";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

const category = Day1EventCategory.AiJourneyConsent;

/** The checked and unchecked action for each permission the screen offers. */
const PERMISSION_ACTIONS: Record<
  keyof AiConsent,
  { checked: Day1EventAction; unchecked: Day1EventAction }
> = {
  allowsDeviceControl: {
    checked: Day1EventAction.AIControlConsentChecked,
    unchecked: Day1EventAction.AIControlConsentUnchecked,
  },
  allowsScreenshotStorage: {
    checked: Day1EventAction.ScreenshotDataStorageConsentChecked,
    unchecked: Day1EventAction.ScreenshotDataStorageConsentUnchecked,
  },
};

/**
 * The screen was shown.
 *
 * Emitted so the accept and decline counts have a denominator. The per-step prompt this
 * replaces emits nothing on mount, so its consent rate cannot be computed.
 */
export const emitJourneyConsentShown = (): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.SectionToggled,
    property: AI_CONSENT_TERMS_VERSION,
  });
};

/** One of the two permissions was ticked or unticked. */
export const emitJourneyConsentPermissionToggled = (
  permission: keyof AiConsent,
  isChecked: boolean
): void => {
  const actions = PERMISSION_ACTIONS[permission];
  trackStructEvent({
    category,
    action: isChecked ? actions.checked : actions.unchecked,
    property: AI_CONSENT_TERMS_VERSION,
  });
};

/**
 * Accept was clicked.
 *
 * Emitted even when validation blocked it, so a hire who tried to continue with one box
 * ticked is countable. The label is what separates the two.
 */
export const emitJourneyConsentAccepted = (isComplete: boolean): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.ClickAgree,
    label: isComplete ? Day1EventLabel.OptedIn : Day1EventLabel.NotOptedIn,
    property: AI_CONSENT_TERMS_VERSION,
  });

  void Logger.info("AI journey consent accepted", undefined, {
    metricType: "AiJourneyConsentAccepted",
    isComplete: String(isComplete),
    termsVersion: AI_CONSENT_TERMS_VERSION,
  });
};

/** Decline was clicked. */
export const emitJourneyConsentDeclined = (): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.AIJourneyConsentDeclined,
    property: AI_CONSENT_TERMS_VERSION,
  });

  void Logger.info("AI journey consent declined", undefined, {
    metricType: "AiJourneyConsentDeclined",
    termsVersion: AI_CONSENT_TERMS_VERSION,
  });
};

/**
 * The hire agreed, the write failed, and they carried on without the assistant. Counted apart
 * from a decline so a backend fault cannot hide inside the opt-out rate.
 */
export const emitJourneyConsentAbandoned = (): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.AIJourneyConsentAbandoned,
    property: AI_CONSENT_TERMS_VERSION,
  });

  void Logger.warn(
    "AI journey consent abandoned after a failed write",
    undefined,
    {
      metricType: "AiJourneyConsentAbandoned",
      termsVersion: AI_CONSENT_TERMS_VERSION,
    }
  );
};
